import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { templateService } from '../services/api';
import TemplateCard from '../components/TemplateCard';

interface Template {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  icon: string;
}

const NicheTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateService.list();
      setTemplates(response.data?.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      enqueueSnackbar('Failed to load templates', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (slug: string) => {
    navigate(`/reels/create?template=${slug}`);
  };

  const handleCreateSeries = (slug: string) => {
    navigate(`/schedules?template=${slug}`);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Niche Templates
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Pre-built series configurations for popular content niches. Choose a template to get started or create an automated series.
        </Typography>

        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
              <TemplateCard
                slug={template.slug}
                name={template.name}
                description={template.description || ''}
                category={template.category || ''}
                icon={template.icon || ''}
                onUseTemplate={handleUseTemplate}
                onCreateSeries={handleCreateSeries}
              />
            </Grid>
          ))}
        </Grid>

        {templates.length === 0 && !loading && (
          <Box textAlign="center" py={5}>
            <Typography variant="h6" color="text.secondary">
              No templates available yet
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default NicheTemplates;
